import { requireSupabase } from "./supabaseClient";

export type PartDraft = {
  id?: string;
  plantId: string;
  internalPn: string;
  oemPn: string;
  description: string;
  manufacturer: string;
  category: string;
  criticality: "A" | "B" | "C";
  stockLocation: string;
  stockQty: number;
  minQty: number;
  lastPrice?: number | null;
  leadTimeDays?: number | null;
  notes: string;
};

export type VendorDraft = {
  id?: string;
  plantId: string;
  name: string;
  website: string;
  contact: string;
  phone: string;
  email: string;
  preferred: boolean;
  notes: string;
};

export type BomDraft = {
  id?: string;
  assetId: string;
  partId: string;
  quantity: number;
  locationNote: string;
  criticalSpare: boolean;
  notes: string;
};

export type PartVendorDraft = {
  id?: string;
  partId: string;
  vendorId: string;
  vendorSku: string;
  manufacturerPartNumber: string;
  quotedPrice?: number | null;
  quoteDate?: string | null;
  leadTimeDays?: number | null;
  preferred: boolean;
  purchaseUrl: string;
  notes: string;
};

function assertRpc<T>(error: { message: string } | null, data: T | null): T {
  if (error) throw new Error(error.message);
  if (data == null) throw new Error("Database operation returned no result.");
  return data;
}

export async function savePartRemote(draft: PartDraft) {
  const client = requireSupabase();
  const { data, error } = await client.rpc("save_part", {
    p_part_id: draft.id ?? null,
    p_plant_id: draft.plantId,
    p_internal_part_number: draft.internalPn,
    p_oem_part_number: draft.oemPn,
    p_description: draft.description,
    p_manufacturer: draft.manufacturer,
    p_category: draft.category,
    p_criticality: draft.criticality,
    p_stock_location: draft.stockLocation,
    p_stock_quantity: draft.stockQty,
    p_minimum_quantity: draft.minQty,
    p_last_price: draft.lastPrice ?? null,
    p_lead_time_days: draft.leadTimeDays ?? null,
    p_notes: draft.notes,
  });
  return assertRpc(error, data as string | null);
}

export async function saveVendorRemote(draft: VendorDraft) {
  const client = requireSupabase();
  const { data, error } = await client.rpc("save_vendor", {
    p_vendor_id: draft.id ?? null,
    p_plant_id: draft.plantId,
    p_company_name: draft.name,
    p_website: draft.website,
    p_contact_name: draft.contact,
    p_phone: draft.phone,
    p_email: draft.email,
    p_preferred: draft.preferred,
    p_notes: draft.notes,
  });
  return assertRpc(error, data as string | null);
}

export async function saveBomItemRemote(draft: BomDraft) {
  const client = requireSupabase();
  const { data, error } = await client.rpc("save_bom_item", {
    p_bom_item_id: draft.id ?? null,
    p_asset_id: draft.assetId,
    p_part_id: draft.partId,
    p_quantity: draft.quantity,
    p_location_note: draft.locationNote,
    p_is_critical_spare: draft.criticalSpare,
    p_notes: draft.notes,
  });
  return assertRpc(error, data as string | null);
}

export async function removeBomItemRemote(id: string, reason = "Removed from asset BOM") {
  const client = requireSupabase();
  const { error } = await client.rpc("remove_bom_item", { p_bom_item_id: id, p_reason: reason });
  if (error) throw new Error(error.message);
}

export async function savePartVendorRemote(draft: PartVendorDraft) {
  const client = requireSupabase();
  const { data, error } = await client.rpc("save_part_vendor", {
    p_part_vendor_id: draft.id ?? null,
    p_part_id: draft.partId,
    p_vendor_id: draft.vendorId,
    p_vendor_sku: draft.vendorSku,
    p_manufacturer_part_number: draft.manufacturerPartNumber,
    p_quoted_price: draft.quotedPrice ?? null,
    p_quote_date: draft.quoteDate ?? null,
    p_lead_time_days: draft.leadTimeDays ?? null,
    p_preferred: draft.preferred,
    p_purchase_url: draft.purchaseUrl,
    p_notes: draft.notes,
  });
  return assertRpc(error, data as string | null);
}

export async function removePartVendorRemote(id: string, reason = "Supplier source removed") {
  const client = requireSupabase();
  const { error } = await client.rpc("remove_part_vendor", { p_part_vendor_id: id, p_reason: reason });
  if (error) throw new Error(error.message);
}

export async function approvePartAlternateRemote(partId: string, alternatePartId: string, note: string) {
  const client = requireSupabase();
  const { error } = await client.rpc("approve_part_alternate", {
    p_part_id: partId,
    p_alternate_part_id: alternatePartId,
    p_approval_note: note,
  });
  if (error) throw new Error(error.message);
}

export async function removePartAlternateRemote(partId: string, alternatePartId: string, reason = "Alternate approval removed") {
  const client = requireSupabase();
  const { error } = await client.rpc("remove_part_alternate", {
    p_part_id: partId,
    p_alternate_part_id: alternatePartId,
    p_reason: reason,
  });
  if (error) throw new Error(error.message);
}
